/**
 * The typed ">>" prefix (#337): `>>park Chase the SSO contract` files the line
 * into the first list whose name starts with "park" once the cursor leaves the
 * line, removing the prefix. No match opens the list picker pre-filtered with
 * the typed name; a bare `>> ` opens it straight away. The list name is
 * compared as plain text - nothing typed is ever turned into a pattern.
 */
import { Plugin, PluginKey, type EditorState } from '@tiptap/pm/state';
import type { Editor } from '@tiptap/core';
import { LINE_SOURCES } from './LineIdExtension';
import type { OpenListPicker } from './NamedListNodes';

export interface PrefixMatch { listId: string | null; name: string; rest: string }

const PREFIX = /^>>(\S*)\s/;

export function matchPrefix(text: string, lists: ReadonlyArray<{ listId: string; name: string }>): PrefixMatch | null {
  const m = PREFIX.exec(text);
  if (!m) return null;
  const word = m[1];
  const lower = word.toLowerCase();
  const hit = word ? lists.find((l) => l.name.toLowerCase().startsWith(lower)) : undefined;
  return { listId: hit?.listId ?? null, name: word, rest: text.slice(m[0].length) };
}

/** Start of the line (paragraph or heading, not in a list) holding `pos`, or null. */
function lineAt(state: EditorState, pos: number): number | null {
  const $p = state.doc.resolve(Math.max(0, Math.min(pos, state.doc.content.size)));
  if (!LINE_SOURCES.has($p.parent.type.name) || $p.depth < 1) return null;
  for (let d = $p.depth - 1; d > 0; d--) if ($p.node(d).type.name === 'namedListItem') return null;
  return $p.before($p.depth);
}

/**
 * `line`: the line holding the cursor. `armed`: a line whose prefix was typed
 * in this editor (only those act - a ">>" already in the notes, such as quoted
 * email, is left alone; review #7). `left`: the armed line the cursor just
 * left. `bare`: a line where ">> " was just typed.
 */
interface PrefixState { line: number | null; armed: number | null; left: number | null; bare: number | null }

export const listPrefixKey = new PluginKey<PrefixState>('listPrefix');

export function listPrefixPlugin(editor: Editor, openPicker: () => OpenListPicker | undefined): Plugin<PrefixState> {
  const coords = (pos: number) => {
    try { const c = editor.view.coordsAtPos(pos); return { x: c.left, y: c.bottom }; } catch { return { x: 0, y: 0 }; }
  };
  return new Plugin<PrefixState>({
    key: listPrefixKey,
    state: {
      init: (_c, state) => ({ line: lineAt(state, state.selection.from), armed: null, left: null, bare: null }),
      apply: (tr, prev, oldState, state) => {
        const line = lineAt(state, state.selection.from);
        const was = prev.line === null ? null : tr.mapping.map(prev.line, -1);
        let armed = prev.armed === null || tr.mapping.mapResult(prev.armed, -1).deleted ? null : tr.mapping.map(prev.armed, -1);
        let bare: number | null = null;
        if (tr.docChanged && line !== null && line === was) {
          const before = oldState.doc.nodeAt(prev.line!)?.textContent ?? '';
          const now = state.doc.nodeAt(line)?.textContent ?? '';
          if (now.startsWith('>> ') && !before.startsWith('>> ')) bare = line;
          else if (PREFIX.test(now) && !PREFIX.test(before)) armed = line;
        }
        const left = armed !== null && was === armed && was !== line && tr.mapping.mapResult(prev.line!, -1).deleted === false ? was : null;
        if (left !== null) armed = null;
        return { line, armed, left, bare };
      },
    },
    view: () => ({
      update: (view) => {
        const ps = listPrefixKey.getState(view.state);
        if (!ps) return;
        if (ps.bare !== null) {
          const at = ps.bare;
          view.dispatch(view.state.tr.delete(at + 1, at + 4));
          openPicker()?.({ positions: [at + 1], filter: '', ...coords(at + 1) });
          return;
        }
        if (ps.left === null) return;
        const at = ps.left;
        const node = view.state.doc.nodeAt(at);
        if (!node || !LINE_SOURCES.has(node.type.name)) return;
        const lists: Array<{ listId: string; name: string }> = [];
        view.state.doc.descendants((n) => {
          if (n.type.name !== 'namedList') return true;
          lists.push({ listId: n.attrs.listId, name: n.attrs.name });
          return false;
        });
        const m = matchPrefix(node.textContent, lists);
        if (!m) return;
        const prefixLen = node.textContent.length - m.rest.length;
        view.dispatch(view.state.tr.delete(at + 1, at + 1 + prefixLen));
        if (m.listId) editor.commands.fileLinesIntoList(m.listId, [at + 1]);
        else openPicker()?.({ positions: [at + 1], filter: m.name, ...coords(at + 1) });
      },
    }),
  });
}
